import React, { memo, useEffect, useState } from 'react';
import {
    endOfDay,
    endOfToday,
    formatISO,
    isBefore,
    startOfDay,
    startOfToday,
} from 'date-fns';
import {useTranslation} from '../../../../config/TranslationProvider';

function leadWithZero(number) {
    if (number < 10) {
        return '0' + number;
    }

    return '' + number;
}

function formatDateRange(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);

    return [
        `${leadWithZero(start.getDate())}.${leadWithZero(start.getMonth() + 1)}.${start.getFullYear()}`,
        `${leadWithZero(end.getDate())}.${leadWithZero(end.getMonth() + 1)}.${end.getFullYear()}`,
    ].join(' - ');
}

export default memo(({inline, startDate, setStartDate, endDate, setEndDate, isHidden, onSpace, ...rest}) => {
    const [value, setValue] = useState(formatDateRange(startDate, endDate));

    // Update value when the start or end date gets changed
    useEffect(() => {
        if (formatDateRange(startDate, endDate) !== value) {
            setValue(formatDateRange(startDate, endDate));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    // Prevents the user from changing the date format or removing the " - " between the dates
    function handleKeyPress(event) {
        if (event.code === 'Space' && onSpace) {
            onSpace();
        }

        const key = event.key;
        if (
            (key.length !== 1 || event.metaKey || event.ctrlKey)
            && key !== 'Backspace'
            && key !== 'Delete'
        ) {
            // Not a new char and not a delete process, don't validate
            return;
        }

        // Get new value
        let value = event.target.value;
        const selectionStart = event.target.selectionStart;
        const selectionEnd = event.target.selectionEnd;

        if (selectionStart !== selectionEnd) {
            // If the user has marked multiple word, remove them
            if (selectionStart > 0) {
                value = value.substring(0, selectionStart) + value.substring(selectionEnd, value.length);
            } else {
                value = value.substring(selectionEnd, value.length);
            }
        } else {
            // Otherwise only remove the key before or after the cursor
            if (key === 'Backspace' && selectionStart > 0) {
                value = value.substring(0, selectionStart - 1) + value.substring(selectionStart, value.length);
            }
            if (key === 'Delete' && selectionStart < value.length - 1) {
                value = value.substring(0, selectionStart) + value.substring(selectionStart + 1, value.length);
            }
        }

        // Insert the new value
        if (key !== 'Backspace' && key !== 'Delete') {
            value = value.substring(0, selectionStart) + key + value.substring(selectionStart, value.length);
        }

        // Finally check if the new value is still valid
        if (!value.match(/^\d{0,2}\.\d{0,2}\.\d{0,4} - \d{0,2}\.\d{0,2}\.\d{0,4}$/)) {
            event.preventDefault();
        }
    }

    function onChange(event) {
        setValue(event.target.value);

        // Update start and end dates if the values are usable
        const [formattedStartDate, formattedEndDate] = event.target.value.split(' - ');
        const formatRegex = new RegExp(/^([0-2]?[1-9]|30|31)\.(0?[1-9]|1[0-2])\.(20\d{2})$/);

        const startDateValues = formatRegex.exec(formattedStartDate);
        if (startDateValues) {
            let start = startOfDay(new Date(
                parseInt(startDateValues[3], 10),
                parseInt(startDateValues[2], 10) - 1,
                parseInt(startDateValues[1], 10),
            ));

            if (!isBefore(start, startOfToday())) {
                setStartDate(formatISO(start));
            }
        }

        const endDateValues = formatRegex.exec(formattedEndDate);
        if (endDateValues) {
            let end = endOfDay(new Date(
                parseInt(endDateValues[3], 10),
                parseInt(endDateValues[2], 10) - 1,
                parseInt(endDateValues[1], 10),
            ));

            if (!isBefore(end, endOfToday())) {
                setEndDate(formatISO(end));
            }
        }
    }

    return (
        <input
            {...rest}
            type="text"
            className={inline ? 'form-control date-input' : 'form-control'}
            value={value}
            onPaste={event => event.preventDefault()}
            onKeyDown={handleKeyPress}
            onChange={onChange}
            aria-label={useTranslation('date_range_time_label')}
            hidden={isHidden}
        />
    );
});
